import { InferenceSession, Tensor } from 'onnxruntime-web'
import { BaClickType, type BaClickInput, type BaImageProcessorOptions, type BaProcessorStatus, type BaRgba, type OnnxModelScale } from '@/typings/ai'
import OnnxHelper from './OnnxHelper'
import { getImageDimensions, getImageInfoFromFile } from '@/utils/image'
import { resize } from '@/utils/photo'
import { cloneDeep } from 'lodash-es'
// 导入WASM模块
// @ts-ignore
import * as maskProcessor from '../../wasm/mask_processor/pkg/mask_processor'

/**
 * 官方demo的代码直接拿来用的
 */
class ImageProcessor {
  status: BaProcessorStatus = ''

  private _session?: InferenceSession

  private _tensor?: Tensor | null
  modelScale?: OnnxModelScale
  private _image?: string

  private _maskColor: BaRgba
  private _mask?: string
  private _maskRaw?: Float32Array
  private _lastPredMask?: Tensor
  
  // 缓存上一次生成的蒙版
  private _previousMask?: string
  
  // WASM模块状态
  private wasmInitialized = false;
  // 缓存离屏Canvas以避免重复创建
  private offscreenCanvas?: OffscreenCanvas;

  constructor(options: BaImageProcessorOptions) {
    const { maskColor = [0, 144, 189, 255] } = options
    this._maskColor = maskColor
    
    // 初始化WASM模块
    this.initWasm();
  }
  
  /**
   * 初始化WASM模块
   */
  private async initWasm() {
    try {
      await maskProcessor.default();
      this.wasmInitialized = true;
      console.log('Mask processor WASM initialized successfully');
    } catch (error) {
      console.error('Failed to initialize WASM module', error);
    }
  }

  async init(onnxModel: string): Promise<BaProcessorStatus> {
    this.status = 'pending'

    try {
      this._session = await OnnxHelper.createInferenceSession(onnxModel)
      this.status = 'ready'
    } catch (error) {
      console.log('Failed to initialize inference session')
      this.status = ''
      throw error
    }

    return this.status
  }

  async loadImage(image: string | File): Promise<{ src: string; modelScale: OnnxModelScale }> {
    // if (this._image) {
    //   URL.revokeObjectURL(this._image)
    // }

    // 将图片控制在1024之内，以免页面卡顿
    const imageSize = await getImageDimensions(image)
    console.log(imageSize)
    let resizeImage
    if (imageSize.width > 1024 || imageSize.height > 1024) {
      resizeImage = await resize(image, 1024)
    } else {
      resizeImage = image
    }

    const { src, width, height } = await getImageInfoFromFile(resizeImage)
    const { scale, uploadScale } = OnnxHelper.handleImageScale({ width, height })

    this.modelScale = {
      width,
      height,
      scaleWidth: Math.round(width * scale),
      scaleHeight: Math.round(height * scale),
      scale,
      onnxScale: scale / uploadScale
    }

    this._image = src
    this._mask = undefined
    this._maskRaw = undefined
    this._lastPredMask = undefined
    this._previousMask = undefined

    return {
      src,
      modelScale: this.modelScale
    }
  }

  async loadTensor(embedding: string | Tensor): Promise<Tensor | null> {
    let tensor: Tensor | null = null

    if (embedding instanceof Tensor) {
      tensor = embedding
    } else {
      tensor = await OnnxHelper.loadTensor(embedding as string)
    }

    this._tensor = tensor
    return tensor
  }

  /**
   * 比较两个蒙版图片的非透明区域大小
   * @param img1 第一张图片的base64
   * @param img2 第二张图片的base64
   * @returns 返回非透明区域像素点更多的图片base64
   */
  async compareNonTransparentArea(img1: string, img2: string): Promise<string> {
    if (this.wasmInitialized) {
      try {
        // 使用WASM实现进行处理
        const [img1Data, img2Data, width, height] = await this.getImageDataFromBase64(img1, img2);
        // 调用WASM方法比较非透明区域
        const img1Larger = maskProcessor.compare_non_transparent_areas(img1Data, img2Data, width, height);
        return img1Larger ? img1 : img2;
      } catch (error) {
        console.error('WASM compareNonTransparentArea failed, falling back to JS implementation', error);
        // 出错时回退到JS实现
        return this.compareNonTransparentAreaJS(img1, img2);
      }
    } else {
      // WASM未初始化，使用JS实现
      return this.compareNonTransparentAreaJS(img1, img2);
    }
  }
  
  /**
   * JS实现的非透明区域比较（作为备用）
   */
  private async compareNonTransparentAreaJS(img1: string, img2: string): Promise<string> {
    return new Promise((resolve) => {
      const image1 = new Image();
      const image2 = new Image();
      
      let img1Loaded = false;
      let img2Loaded = false;
      
      const processImages = () => {
        if (!img1Loaded || !img2Loaded) return;
        
        const canvas1 = document.createElement('canvas');
        const ctx1 = canvas1.getContext('2d');
        canvas1.width = image1.width;
        canvas1.height = image1.height;
        ctx1!.drawImage(image1, 0, 0);
        
        const canvas2 = document.createElement('canvas');
        const ctx2 = canvas2.getContext('2d');
        canvas2.width = image2.width;
        canvas2.height = image2.height;
        ctx2!.drawImage(image2, 0, 0);
        
        const imgData1 = ctx1!.getImageData(0, 0, canvas1.width, canvas1.height);
        const imgData2 = ctx2!.getImageData(0, 0, canvas2.width, canvas2.height);
        
        let nonTransparentPixels1 = 0;
        let nonTransparentPixels2 = 0;
        
        // 计算非透明像素点数量
        for (let i = 3; i < imgData1.data.length; i += 4) {
          if (imgData1.data[i] > 0) nonTransparentPixels1++;
        }
        
        for (let i = 3; i < imgData2.data.length; i += 4) {
          if (imgData2.data[i] > 0) nonTransparentPixels2++;
        }
        
        console.log('非透明区域比较:', nonTransparentPixels1, nonTransparentPixels2);
        
        // 返回非透明区域更大的图片
        if (nonTransparentPixels1 >= nonTransparentPixels2) {
          resolve(img1);
        } else {
          resolve(img2);
        }
      };
      
      image1.onload = () => {
        img1Loaded = true;
        processImages();
      };
      
      image2.onload = () => {
        img2Loaded = true;
        processImages();
      };
      
      image1.src = img1;
      image2.src = img2;
    });
  }

  /**
   * 比较新生成的蒙版与单点蒙版的非透明区域，返回更大的区域
   * @param allPointsMask 所有点生成的蒙版
   * @param singlePointMask 只有当前点生成的蒙版
   * @param previousMask 之前的蒙版
   * @returns 最终应该使用的蒙版
   */
  async compareAndGetBetterMask(allPointsMask: string, singlePointMask: string, previousMask: string): Promise<string> {
    // 先合并之前的蒙版和单点蒙版
    const mergedMask = await this.mergeNonTransparentAreas(previousMask, singlePointMask);
    
    // 比较合并后的蒙版与所有点生成的蒙版，返回非透明区域更大的
    return await this.compareNonTransparentArea(allPointsMask, mergedMask);
  }

  /**
   * 合并两张图片的非透明区域
   * @param img1 第一张图片的base64
   * @param img2 第二张图片的base64
   * @returns 合并后的图片base64
   */
  async mergeNonTransparentAreas(img1: string, img2: string): Promise<string> {
    if (this.wasmInitialized) {
      try {
        // 使用WASM实现进行处理
        const [img1Data, img2Data, width, height] = await this.getImageDataFromBase64(img1, img2);
        // 调用WASM方法合并非透明区域
        const resultData = maskProcessor.merge_masks(img1Data, img2Data, width, height);
        // 将结果转回base64
        return await this.imageDataToBase64(resultData, width, height);
      } catch (error) {
        console.error('WASM mergeNonTransparentAreas failed, falling back to JS implementation', error);
        // 出错时回退到JS实现
        return this.mergeNonTransparentAreasJS(img1, img2);
      }
    } else {
      // WASM未初始化，使用JS实现
      return this.mergeNonTransparentAreasJS(img1, img2);
    }
  }
  
  /**
   * JS实现的非透明区域合并（作为备用）
   */
  private async mergeNonTransparentAreasJS(img1: string, img2: string): Promise<string> {
    return new Promise((resolve) => {
      const image1 = new Image();
      const image2 = new Image();
      
      let img1Loaded = false;
      let img2Loaded = false;
      
      const processImages = () => {
        if (!img1Loaded || !img2Loaded) return;
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = image1.width;
        canvas.height = image1.height;
        
        // 绘制第二张图片作为基础
        ctx!.drawImage(image2, 0, 0);
        const imgData2 = ctx!.getImageData(0, 0, canvas.width, canvas.height);
        
        // 清除画布
        ctx!.clearRect(0, 0, canvas.width, canvas.height);
        
        // 绘制第一张图片
        ctx!.drawImage(image1, 0, 0);
        const imgData1 = ctx!.getImageData(0, 0, canvas.width, canvas.height);
        
        // 合并非透明区域，保留原始颜色
        for (let i = 0; i < imgData1.data.length; i += 4) {
          // 如果第一张图片在这个位置是非透明的
          if (imgData1.data[i + 3] > 0) {
            // 保持第一张图片的颜色和不透明度
            continue;
          } 
          // 如果第二张图片在这个位置是非透明的
          else if (imgData2.data[i + 3] > 0) {
            // 使用第二张图片的颜色
            imgData1.data[i] = imgData2.data[i];
            imgData1.data[i + 1] = imgData2.data[i + 1];
            imgData1.data[i + 2] = imgData2.data[i + 2];
            imgData1.data[i + 3] = imgData2.data[i + 3];
          }
        }
        
        // 将合并后的图像数据绘制回画布
        ctx!.putImageData(imgData1, 0, 0);
        
        // 返回合并后的图片
        resolve(canvas.toDataURL('image/png'));
      };
      
      image1.onload = () => {
        img1Loaded = true;
        processImages();
      };
      
      image2.onload = () => {
        img2Loaded = true;
        processImages();
      };
      
      image1.src = img1;
      image2.src = img2;
    });
  }

  /**
   * 从第一张图片中减去第二张图片的非透明区域
   * @param baseImg 基础图片base64
   * @param subtractImg 要减去的图片base64
   * @returns 处理后的图片base64
   */
  async subtractNonTransparentAreas(baseImg: string, subtractImg: string): Promise<string> {
    if (this.wasmInitialized) {
      try {
        // 使用WASM实现进行处理
        const [baseData, subtractData, width, height] = await this.getImageDataFromBase64(baseImg, subtractImg);
        // 调用WASM方法减去非透明区域
        const resultData = maskProcessor.subtract_masks(baseData, subtractData, width, height);
        // 将结果转回base64
        return await this.imageDataToBase64(resultData, width, height);
      } catch (error) {
        console.error('WASM subtractNonTransparentAreas failed, falling back to JS implementation', error);
        // 出错时回退到JS实现
        return this.subtractNonTransparentAreasJS(baseImg, subtractImg);
      }
    } else {
      // WASM未初始化，使用JS实现
      return this.subtractNonTransparentAreasJS(baseImg, subtractImg);
    }
  }
  
  /**
   * JS实现的非透明区域减法（作为备用）
   */
  private async subtractNonTransparentAreasJS(baseImg: string, subtractImg: string): Promise<string> {
    return new Promise((resolve) => {
      const image1 = new Image();
      const image2 = new Image();
      
      let img1Loaded = false;
      let img2Loaded = false;
      
      const processImages = () => {
        if (!img1Loaded || !img2Loaded) return;
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = image1.width;
        canvas.height = image1.height;
        
        // 绘制第一张图片（基础图片）
        ctx!.drawImage(image1, 0, 0);
        const imgData1 = ctx!.getImageData(0, 0, canvas.width, canvas.height);
        
        // 清除画布
        ctx!.clearRect(0, 0, canvas.width, canvas.height);
        
        // 绘制第二张图片（要减去的图片）
        ctx!.drawImage(image2, 0, 0);
        const imgData2 = ctx!.getImageData(0, 0, canvas.width, canvas.height);
        
        // 从第一张图片中减去第二张图片的非透明区域
        for (let i = 0; i < imgData1.data.length; i += 4) {
          // 如果第二张图片在这个位置是非透明的，则在第一张图片对应位置设为透明
          if (imgData2.data[i + 3] > 128) { // 使用阈值以处理半透明区域
            imgData1.data[i + 3] = 0; // 设置为完全透明
          }
        }
        
        // 将处理后的图像数据绘制回画布
        ctx!.putImageData(imgData1, 0, 0);
        
        // 返回处理后的图片
        resolve(canvas.toDataURL('image/png'));
      };
      
      image1.onload = () => {
        img1Loaded = true;
        processImages();
      };
      
      image2.onload = () => {
        img2Loaded = true;
        processImages();
      };
      
      image1.src = baseImg;
      image2.src = subtractImg;
    });
  }
  
  /**
   * 从两个base64图像获取像素数据
   */
  private async getImageDataFromBase64(img1: string, img2: string): Promise<[Uint8Array, Uint8Array, number, number]> {
    return new Promise((resolve, reject) => {
      const image1 = new Image();
      const image2 = new Image();
      
      let img1Loaded = false;
      let img2Loaded = false;
      
      const processImages = () => {
        if (!img1Loaded || !img2Loaded) return;
        
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          
          if (!ctx) {
            reject(new Error('无法创建Canvas 2D上下文'));
            return;
          }
          
          canvas.width = image1.width;
          canvas.height = image1.height;
          
          // 获取第一张图片数据
          ctx.drawImage(image1, 0, 0);
          const imgData1 = ctx.getImageData(0, 0, image1.width, image1.height);
          
          // 获取第二张图片数据
          ctx.clearRect(0, 0, image1.width, image1.height);
          ctx.drawImage(image2, 0, 0);
          const imgData2 = ctx.getImageData(0, 0, image1.width, image1.height);
          
          resolve([
            new Uint8Array(imgData1.data.buffer),
            new Uint8Array(imgData2.data.buffer),
            image1.width,
            image1.height
          ]);
        } catch (e) {
          reject(e);
        }
      };
      
      image1.onload = () => {
        img1Loaded = true;
        processImages();
      };
      
      image2.onload = () => {
        img2Loaded = true;
        processImages();
      };
      
      image1.onerror = () => reject(new Error('无法加载第一张图片'));
      image2.onerror = () => reject(new Error('无法加载第二张图片'));
      
      image1.src = img1;
      image2.src = img2;
    });
  }
  
  /**
   * 将处理后的像素数据转回base64
   */
  private async imageDataToBase64(data: Uint8Array, width: number, height: number): Promise<string> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('无法创建Canvas 2D上下文');
    }
    
    canvas.width = width;
    canvas.height = height;
    
    const imgData = new ImageData(
      new Uint8ClampedArray(data), 
      width, 
      height
    );
    
    ctx.putImageData(imgData, 0, 0);
    
    // 返回base64编码的图像
    return canvas.toDataURL('image/png');
  }

  async generateMask(clicks: Array<BaClickInput>, type: 'hover' | 'click' = 'click'): Promise<string> {
    if (
      this.status !== 'ready' ||
      this._session === undefined ||
      this._tensor === undefined ||
      this.modelScale === undefined
    ) {
      console.log('Image processor not ready')
      return ''
    }

    const feeds = OnnxHelper.prepareOnnxModelFeeds({
      clicks,
      tensor: this._tensor!,
      modelScale: this.modelScale
      // prevPredMask: this._lastPredMask,
    })

    if (feeds === undefined) {
      throw new Error('Error generating mask')
    }

    return new Promise((resolve) => {
      setTimeout(async () => {
        const results = await this._session!.run(feeds)

        const output = results[this._session!.outputNames[0]]
        const width = output.dims[3]
        const height = output.dims[2]

        // 生成蒙版图片
        const generatedMask = OnnxHelper.onnxMaskToImage({
          input: output.data,
          width: width,
          height: height,
          maskColor: this._maskColor
        }).src

        // 点击生成蒙版时，处理蒙版缓存和比较
        if (type === 'click') {
          if (this._previousMask === undefined) {
            // 第一次点击，直接保存蒙版
            this._previousMask = generatedMask;
          } else if (clicks.length > 0) {
            const lastClick = clicks[clicks.length - 1];

            console.log('lastClick', lastClick)
            
            // 确保模型和张量都准备好
            if (this._session && this._tensor && this.modelScale) {
              // 只处理添加或删除选区的情况
              if (lastClick.clickType === BaClickType.add ) { // BaClickType.add 添加选区
                // 准备单点蒙版的输入
                const singleClickFeeds = OnnxHelper.prepareOnnxModelFeeds({
                  clicks: [lastClick],
                  tensor: this._tensor,
                  modelScale: this.modelScale
                });
                
                if (singleClickFeeds !== undefined) {
                  // 生成单点蒙版
                  const singleResults = await this._session.run(singleClickFeeds);
                  const singleOutput = singleResults[this._session.outputNames[0]];
                  const singleMask = OnnxHelper.onnxMaskToImage({
                    input: singleOutput.data,
                    width: singleOutput.dims[3],
                    height: singleOutput.dims[2],
                    maskColor: this._maskColor
                  }).src;

                  console.log('开始比较蒙版...');
                  const startTime = performance.now();
                  
                  // 比较并获取更好的蒙版
                  const betterMask = await this.compareAndGetBetterMask(generatedMask, singleMask, this._previousMask);
                  
                  console.log(`蒙版处理完成，耗时: ${performance.now() - startTime}ms`);
                  
                  // 更新前一次蒙版
                  this._previousMask = betterMask;
                  
                  resolve(betterMask);
                  return;
                }
              } else if (lastClick.clickType === BaClickType.remove) { // BaClickType.remove 减少选区
                const lastClickClone = cloneDeep(lastClick)
                lastClickClone.clickType = BaClickType.add
                // 准备单点蒙版的输入
                const singleClickFeeds = OnnxHelper.prepareOnnxModelFeeds({
                  clicks: [lastClickClone],
                  tensor: this._tensor,
                  modelScale: this.modelScale
                });
                
                if (singleClickFeeds !== undefined && this._previousMask) {
                  // 生成单点蒙版
                  const singleResults = await this._session.run(singleClickFeeds);
                  const singleOutput = singleResults[this._session.outputNames[0]];
                  const singleMask = OnnxHelper.onnxMaskToImage({
                    input: singleOutput.data,
                    width: singleOutput.dims[3],
                    height: singleOutput.dims[2],
                    maskColor: this._maskColor
                  }).src;

                  console.log('开始减去蒙版...');
                  const startTime = performance.now();
                  
                  // 从上一次的蒙版中减去单点生成的蒙版
                  const subtractedMask = await this.subtractNonTransparentAreas(this._previousMask, singleMask);
                  
                  console.log(`蒙版处理完成，耗时: ${performance.now() - startTime}ms`);
                  
                  // 更新前一次蒙版
                  this._previousMask = subtractedMask;
                  
                  resolve(subtractedMask);
                  return;
                }
              }
            }
          }
          
          // 默认情况或处理失败时，使用当前生成的蒙版
          this._previousMask = generatedMask;
        }

        resolve(generatedMask)
      }, 2)
    })
  }

  resetMask(): void {
    this._mask = undefined
    this._maskRaw = undefined
    this._lastPredMask = undefined
    this._previousMask = undefined
  }

  destroy(): void {
    // 清理资源的逻辑可以在这里添加
    this._previousMask = undefined;
    this._session = undefined;
    this._tensor = undefined;
  }
}

export default ImageProcessor
